import {
  AddressVerifiedStatus,
  DenialReason,
  Lead,
  Product,
  ProductOffering,
} from 'src/declarations';

import {
  OdProtosSellReceptionData_SellerInput_AddressVerifiedStatus as GraphQLAddressVerifiedStatus,
  OdProtosSellReceptionData_SellerInput_Channel as GraphQLChannel,
  OdProtosSellCommon_DenialReason as GraphQLDenialReason,
  OdProtosCasaData_Result,
  OdProtosSellCommon_Product,
} from '../__generated__/globalTypes';
import {
  GetSellerInputQuery_reception_getSellerInput_sellerInput,
  GetSellerInputQuery_reception_getSellerInput_sellerInput_address as GraphQLAddress,
  GetSellerInputQuery_reception_getSellerInput_sellerInput_productOfferings as GraphQLProductOffering,
} from './queries/__generated__/GetSellerInputQuery';

type SellerInput = Omit<
  GetSellerInputQuery_reception_getSellerInput_sellerInput,
  'featureFlagsAmplitude'
>;

/**
 * Temporary transformation from new GetSellerInputQuery response to old Lead
 *
 * reception-fe makes calls to a variety of reception-bff endpoints that all return a Lead (e.g.
 * LeadCreate, etc.).
 *
 * In order to transition these endpoints to fetch the needed data via Athena transform the Athena
 * response to match the response given by reception-bff for parity. Once all Lead-related queries
 * have been moved from reception-bff to Athena we can remove this transformation and update
 * reception-fe to use the modern lead concept.
 *
 * TODO: remove this transform once all Lead-related endpoints have been moved off of reception-bff
 */
export async function constructLead(sellerInput: SellerInput): Promise<Lead> {
  const opendoorDirectDenialReason = getOpendoorDirectDenialReason(sellerInput);

  const address = sellerInput.address ? convertAddress(sellerInput.address) : null;

  return {
    address,
    addressVerifiedStatus: addressVerifiedStatusEnumToAddressVerifiedStringUnion(
      sellerInput.addressVerifiedStatus,
    ),
    addressUuid: sellerInput.addressUuid,
    answer_prefills: JSON.parse(sellerInput.answerPrefillsJson ?? '{}'),
    answers: JSON.parse(sellerInput.answersJson ?? '{}'),
    channel: channelEnumToString(sellerInput.channel),
    customerUuid: sellerInput.customerUuid,
    denial_code: opendoorDirectDenialReason,
    denial_reason: generateDenialReasonDisplayString({
      denialReason: opendoorDirectDenialReason,
      maxLotSqFt: sellerInput?.opendoorDirectBuyboxThresholds?.maxLotSqFt,
      minYearBuilt: sellerInput?.opendoorDirectBuyboxThresholds?.minYearBuilt,
      addressStreet: address?.street || '',
    }),
    dwellings_detail: {
      last_mls_listing: {
        is_listed_and_eligible: sellerInput?.isListed,
        list_date: sellerInput?.homeDetails?.lastListing?.listDate,
        listing_id: sellerInput?.homeDetails?.lastListing?.listingId,
        listing_photos: sellerInput?.homeDetails?.lastListing?.listingPhotos,
        realtor_name: sellerInput?.homeDetails?.lastListing?.realtorName,
        realtor_email: sellerInput?.homeDetails?.lastListing?.realtorEmail,
      },
    },
    has_homebuilder_sales_associate: sellerInput?.partnershipReferrals?.some(
      ({ type, userId }) => type === 'home_builder' && !!userId,
    ),
    home_value_cents: sellerInput?.homeValue?.headlinePrice?.value,
    home_value_comps: sellerInput?.homeValue?.comps,
    last_listing_photos: sellerInput?.homeDetails?.photos,
    market: sellerInput.region,
    near_flood_zone: !!sellerInput?.homeDetails?.nearFloodZone,
    partnerships: sellerInput?.partnershipReferrals,
    product_offerings: getProductOfferings(sellerInput),
    seller_flow_last_completed_slug: sellerInput?.sellerFlowLastCompletedSlug,
    seller_flow_uuid: sellerInput.sellerFlowUuid,
    state: getLeadState(
      sellerInput,
      !!address?.is_missing_unit_number,
      !!address?.is_invalid_unit_number,
    ),
    token: sellerInput?.uuid,
  };
}

export const getProductOfferings = (sellerInput: SellerInput): Array<ProductOffering> =>
  sellerInput.productOfferings
    // effectively flat map due to filter at end
    ?.map(
      ({
        deniedAt,
        denialReason,
        denialReasons,
        matchedAt,
        product,
        qualifiedAt,
      }: GraphQLProductOffering): ProductOffering | null => {
        const productString = productEnumToString(product);

        if (!productString) {
          return null;
        }

        return {
          product: productString,
          matched: !!matchedAt,
          denied: !!deniedAt,
          denialReason: denialReasonEnumToDenialReasonString(denialReason),
          qualified: !!qualifiedAt,
          denialReasons: denialReasons
            ?.map(denialReasonEnumToDenialReasonString)
            ?.filter((dr) => !!dr),
        };
      },
    )
    .filter((po): po is ProductOffering => po !== null);

export const getProductOfferingsMap = (
  sellerInput: SellerInput,
): Partial<Record<Product, ProductOffering>> => {
  const all: Partial<Record<Product, ProductOffering>> = {};
  getProductOfferings(sellerInput).forEach((po) => {
    all[po.product] = po;
  });
  return all;
};

export const getLeadState = (
  sellerInput: SellerInput,
  isMissingUnitNumber: boolean,
  isInvalidUnitNumber: boolean,
): string => {
  const needsToVerifyAddress =
    !sellerInput.addressVerifiedStatus &&
    sellerInput?.addressVerifiedStatus !== GraphQLAddressVerifiedStatus.NOT_VERIFIED;

  const needsToVerifyUnitNumber =
    (isMissingUnitNumber || isInvalidUnitNumber) &&
    sellerInput?.addressVerifiedStatus !== GraphQLAddressVerifiedStatus.CONFIRMED_VERIFIED;

  if (needsToVerifyAddress || needsToVerifyUnitNumber) {
    return 'verifying_address';
  } else if (getOpendoorDirectDenialReason(sellerInput)) {
    // make sure this comes after address/unit number validation
    return 'denied';
  } else if (sellerInput.isListed) {
    return 'verifying_listing';
  } else if (getOpendoorDirectProductOffering(sellerInput)?.qualifiedAt) {
    return 'needs_attention';
  }

  return 'inputting_answers';
};

// TODO: consolidate Address types (here, declarations, GraphQL) in follow-up PR
const convertAddress = ({
  city,
  latitude,
  longitude,
  postalCode,
  result,
  state,
  street,
  unitNumber,
  uuid,
}: GraphQLAddress): {
  city: string;
  is_missing_unit_number: boolean;
  is_invalid_unit_number: boolean;
  latitude: number;
  longitude: number;
  postal_code: string;
  state: string;
  street: string;
  street1: string;
  unit: string;
  uuid: string;
} => {
  return {
    city,
    is_missing_unit_number: result?.includes(OdProtosCasaData_Result.ADDRESS_MISSING_SUB_PREMISE),
    is_invalid_unit_number: result?.includes(OdProtosCasaData_Result.ADDRESS_INVALID_SUB_PREMISE),
    latitude,
    longitude,
    postal_code: postalCode,
    state,
    street: `${street}${unitNumber ? ` #${unitNumber}` : ''}`,
    street1: street,
    unit: unitNumber,
    uuid,
  };
};

const getOpendoorDirectProductOffering = (
  sellerInput: SellerInput,
): GraphQLProductOffering | undefined =>
  sellerInput.productOfferings.find(
    ({ product }) => product === OdProtosSellCommon_Product.OPENDOOR_DIRECT,
  );

export const getOpendoorDirectDenialReason = (sellerInput: SellerInput): DenialReason | undefined =>
  denialReasonEnumToDenialReasonString(getOpendoorDirectProductOffering(sellerInput)?.denialReason);

export const generateDenialReasonDisplayString = ({
  denialReason,
  maxLotSqFt,
  minYearBuilt,
  addressStreet,
}: {
  denialReason: DenialReason | undefined;
  maxLotSqFt: number | null | undefined;
  minYearBuilt: number | null | undefined;
  addressStreet: string;
}): string | undefined => {
  if (denialReason === undefined) {
    return;
  }

  if (['valuation_too_high', 'valuation_too_low'].includes(denialReason)) {
    return "We believe your home's value falls outside the range we purchase within.";
  } else if (denialReason === 'lot_sq_ft' && maxLotSqFt) {
    return `We're currently only buying homes with a lot size less than ${maxLotSqFtToText(
      maxLotSqFt,
    )}.`;
  } else if (denialReason === 'year_built' && minYearBuilt) {
    return `We currently are not buying homes built before ${minYearBuilt}.`;
  } else if (denialReason === 'active_offer') {
    return `It looks like there's already an active offer${
      addressStreet ? ` for ${addressStreet}` : ''
    }! If you originally requested this offer, please check your email for your offer details.`;
  } else if (denialReason === 'preparing_offer') {
    return `It looks like we're already working on an offer${
      addressStreet ? ` for ${addressStreet}` : ''
    }! You should get an email with your offer details within 24-48 hours of your submission.`;
  } else if (denialReason in denialReasonToDisplayStringMap) {
    return denialReasonToDisplayString(denialReason);
  }

  return "According to property records, your home doesn't meet our current purchasing criteria.";
};

const maxLotSqFtToText = (maxLotSqFt: number): string => {
  if (maxLotSqFt === 21780) {
    return 'half an acre';
  } else if (maxLotSqFt === 43560) {
    return 'an acre';
  }

  return `${maxLotSqFt.toLocaleString()} ft²`;
};

export const addressVerifiedStatusEnumToAddressVerifiedStringUnion = (
  addressVerifiedStatusEnum: GraphQLAddressVerifiedStatus,
): AddressVerifiedStatus | undefined => {
  const addressVerifiedStatusEnumToStringMap: {
    [key in GraphQLAddressVerifiedStatus]?: AddressVerifiedStatus;
  } = {
    [GraphQLAddressVerifiedStatus.NOT_VERIFIED]: 'not_verified',
    [GraphQLAddressVerifiedStatus.CONFIRMED_VERIFIED]: 'confirmed_verified',
    [GraphQLAddressVerifiedStatus.AUTO_VERIFIED]: 'auto_verified',
  };

  return addressVerifiedStatusEnumToStringMap[addressVerifiedStatusEnum];
};

const productEnumToString = (productEnum: OdProtosSellCommon_Product): Product | undefined => {
  const productEnumToStringMap: { [key in OdProtosSellCommon_Product]?: Product } = {
    [OdProtosSellCommon_Product.AGENT_REFERRAL]: 'agent_referral',
    [OdProtosSellCommon_Product.OPENDOOR_DIRECT]: 'opendoor_direct',
    [OdProtosSellCommon_Product.REIT]: 'reit',
    [OdProtosSellCommon_Product.SELL_WITH_EXCLUSIVES]: 'sell_with_exclusives',
  };

  return productEnumToStringMap[productEnum];
};

const channelEnumToString = (channelEnum: GraphQLChannel): string => channelEnum.toLowerCase();

// ref - https://stackoverflow.com/a/48450434/5121636
const stringToEnum = <ET, T>(enumObj: ET, str: keyof ET): T => enumObj[str as string];

export const channelStringToEnum = (channel?: string): GraphQLChannel =>
  channel
    ? stringToEnum<typeof GraphQLChannel, GraphQLChannel>(
        GraphQLChannel,
        channel.toUpperCase() as keyof typeof GraphQLChannel,
      )
    : GraphQLChannel.UNSET_CHANNEL;

export const denialReasonEnumToDenialReasonString = (
  denialEnumValue?: GraphQLDenialReason,
): DenialReason | undefined => {
  const denialEnumToDenialReasonMap: { [key in GraphQLDenialReason]?: DenialReason } = {
    [GraphQLDenialReason.ACTIVE_OFFER]: 'active_offer',
    [GraphQLDenialReason.ADDRESS]: 'address',
    [GraphQLDenialReason.AGE_RESTRICTED_COMMUNITY]: 'age_restricted_community',
    [GraphQLDenialReason.AGE_RESTRICTED_SEASONAL]: 'age_restricted_seasonal',
    [GraphQLDenialReason.AGENT_RELATION]: 'agent_relation',
    [GraphQLDenialReason.ASBESTOS_SIDING]: 'asbestos_siding',
    [GraphQLDenialReason.BEFORE_1960]: 'before_1960',
    [GraphQLDenialReason.BUYER_UNAVAILABLE]: 'buyer_unavailable',
    [GraphQLDenialReason.COVID_19]: 'covid_19',
    [GraphQLDenialReason.DENIED_FOR_SELL_WITH_UPSIDE]: 'denied_for_sell_with_upside',
    [GraphQLDenialReason.DENIED_FOR_OPENDOOR_DIRECT]: 'denied_for_opendoor_direct',
    [GraphQLDenialReason.DUPLICATE_LEAD]: 'duplicate_lead',
    [GraphQLDenialReason.DWELLING_TYPE]: 'dwelling_type',
    [GraphQLDenialReason.FEES_TOO_HIGH]: 'fees_too_high',
    [GraphQLDenialReason.FIRE_DAMAGE]: 'fire_damage',
    [GraphQLDenialReason.FLOOD_HISTORY]: 'flood_history',
    [GraphQLDenialReason.FLOOD_ZONE]: 'flood_zone',
    [GraphQLDenialReason.FOUNDATION]: 'foundation',
    [GraphQLDenialReason.GATED_COMMUNITY]: 'gated_community',
    [GraphQLDenialReason.INELIGIBLE_HOA]: 'ineligible_hoa',
    [GraphQLDenialReason.IN_CONTRACT_WITH_OPENDOOR]: 'in_contract_with_opendoor',
    [GraphQLDenialReason.LACK_OF_ADEQUATE_COMPS]: 'lack_of_adequate_comps',
    [GraphQLDenialReason.LEASED_SOLAR_PANELS]: 'leased_solar',
    [GraphQLDenialReason.LISTED_BY_OPENDOOR]: 'listed_by_opendoor',
    [GraphQLDenialReason.LIVESTOCK]: 'livestock',
    [GraphQLDenialReason.LOT_SQ_FT]: 'lot_sq_ft',
    [GraphQLDenialReason.MOBILE_MANUFACTURED_HOME]: 'mobile_manufactured_home',
    [GraphQLDenialReason.NON_BUYBOX_DWELLING_TYPE]: 'non_buybox_dwelling_type',
    [GraphQLDenialReason.NON_SERIOUS_SELLER]: 'non_serious_seller',
    [GraphQLDenialReason.NOT_IN_AUTODENIAL_LISTING_EXP]: 'not_in_autodenial_listing_exp',
    [GraphQLDenialReason.NOT_IN_SELL_WITH_UPSIDE_PILOT]: 'not_in_sell_with_upside_pilot',
    [GraphQLDenialReason.NOT_IN_SIDE_BY_SIDE_EXP]: 'not_in_side_by_side_exp',
    [GraphQLDenialReason.OFFER_OUTLIER]: 'offer_outlier',
    [GraphQLDenialReason.OWNED_BY_OPENDOOR]: 'owned_by_opendoor',
    [GraphQLDenialReason.OTHER_DENIAL]: 'other',
    [GraphQLDenialReason.OTHER_HIGH_RISK]: 'other_high_risk',
    [GraphQLDenialReason.PARTNERSHIP]: 'partnership',
    [GraphQLDenialReason.POLYBUTYLENE_PIPING]: 'polybutylene_piping',
    [GraphQLDenialReason.PREPARING_OFFER]: 'preparing_offer',
    [GraphQLDenialReason.PRIVATE_POOL]: 'private_pool',
    [GraphQLDenialReason.QUALIFIED_FOR_LWOD]: 'qualified_for_lwod',
    [GraphQLDenialReason.RESERVED_FOR_EXTERNAL_BROKER]: 'reserved_for_external_broker',
    [GraphQLDenialReason.SELLER_TRIAGE]: 'seller_triage',
    [GraphQLDenialReason.SEPTIC]: 'septic',
    [GraphQLDenialReason.SHARED_ENTRANCE]: 'shared_entrance',
    [GraphQLDenialReason.SHARED_LOT]: 'shared_lot',
    [GraphQLDenialReason.TENANT_OCCUPIED]: 'tenant_occupied',
    [GraphQLDenialReason.TRADE_IN]: 'trade_in',
    [GraphQLDenialReason.UNABLE_TO_VALUE]: 'unable_to_value',
    [GraphQLDenialReason.UNDERGROUND_OIL_TANK]: 'underground_oil_tank',
    [GraphQLDenialReason.UNPERMITTED_ADDITION]: 'unpermitted_addition',
    [GraphQLDenialReason.VALUATION_TOO_HIGH]: 'valuation_too_high',
    [GraphQLDenialReason.VALUATION_TOO_LOW]: 'valuation_too_low',
    [GraphQLDenialReason.VALUATION_TOO_LOW_LISTED]: 'valuation_too_low_listed',
    [GraphQLDenialReason.WELL_WATER]: 'well_water',
    [GraphQLDenialReason.WORKING_WITH_HOMEBUILDER]: 'working_with_homebuilder',
    [GraphQLDenialReason.YEAR_BUILT]: 'year_built',
    [GraphQLDenialReason.GUARD_GATED_COMMUNITY]: 'guard_gated_community',
  };

  return denialEnumValue ? denialEnumToDenialReasonMap[denialEnumValue] : undefined;
};

const denialReasonToDisplayStringMap: { [key in DenialReason]?: string } = {
  address: "We're not in your area yet, but we are expanding quickly.",
  age_restricted_community: 'We currently are not buying homes in age-restricted communities.',
  asbestos_siding: 'We currently are not buying homes with asbestos siding.',
  covid_19:
    'Given the uncertainty surrounding the impact of COVID-19 across our markets, we are temporarily pausing the purchase of homes.',
  dwelling_type:
    "We're currently only buying single-family homes and condominiums (in select areas).",
  flood_history: 'We currently are not buying homes that have a history of flooding.',
  flood_zone: 'We currently are not buying homes in or near flood zones.',
  gated_community: 'We currently are not buying homes in gated communities.',
  guard_gated_community: 'We currently are not buying homes in guard-gated communities.',
  in_contract_with_opendoor: 'An offer has already been requested for this home.',
  leased_solar: 'We currently are not buying homes with non-prepaid leased solar panels.',
  listed_by_opendoor: 'This home has already been sold to Opendoor.',
  other:
    'We make competitive offers by analyzing comparable home sales and market data in the area. In some cases like yours, we have insufficient information, such as lack of strong comps sold recently, or other high risk factors that make it difficult for us to value your home at this time.',
  owned_by_opendoor: 'This home has already been sold to Opendoor.',
  polybutylene_piping: 'We currently are not buying homes with polybutylene piping.',
  private_pool: 'We currently are not buying homes with pools.',
  septic: 'We currently are not buying homes with septic systems.',
  unpermitted_addition: 'We currently are not buying homes with unpermitted additions.',
  well_water: 'We currently are not buying homes with private wells as the primary water source.',
};

const denialReasonToDisplayString = (denialReason: DenialReason): string | undefined =>
  denialReasonToDisplayStringMap[denialReason];
